/**
 * ratesAndThresholds.ts
 * Central source for Age Pension, tax, deeming, gifting, downsizer, and related rates.
 * Update this file when rates change (indexation: 20 March & 20 September for pension; quarterly for MPIR).
 * Ported verbatim from frontend/src/constants/ratesAndThresholds.js — keep both in sync until the
 * frontend copy is retired (see the calculation-engine migration plan).
 */

/**
 * WHEN this file was last brought up to date, and when it next falls due.
 *
 * Rates in here are hand-maintained against published figures, so the only thing standing between a
 * client and a stale calculation is somebody remembering. `RATES_REVIEW_BY` is that reminder with teeth:
 * `ratesFreshness.test.ts` fails once the date passes, naming what has changed and where to update it.
 * A failing build is a nuisance; quoting a superseded Age Pension rate to a client is worse.
 *
 * The dates that matter, so the next reviewer does not have to rediscover them:
 *   - Age Pension rates and both means-test limits — indexed 20 MARCH and 20 SEPTEMBER
 *   - Aged care fee caps and MTA thresholds — those two dates AND 1 JULY (`AGED_CARE_SCHEDULE.effectiveDate`).
 *     The Department publishes a 1 July schedule that moves the means-test income thresholds, the
 *     fourth asset threshold and the maximum RAD even though the pension does not move. Missed once,
 *     July–September 2026; see agedCareSchedule.ts.
 *   - MPIR — QUARTERLY, on 1 July / 1 October / 1 January / 1 April (`mpiQuarterLabel`)
 *   - Tax brackets, Medicare thresholds, LITO and SAPTO — 1 JULY, with the Budget
 *
 * `RATES_FINANCIAL_YEAR` is what the assumptions page shows the reader, so the page cannot drift from
 * the figures beneath it — which it had, describing 2026-27 brackets under a 2025-26 heading.
 */
export const RATES_FINANCIAL_YEAR = '2026-27';

/**
 * The next scheduled indexation after the figures below were set: 20 September 2026, when the Age
 * Pension and the aged care caps both move. (MPIR moves first, on 1 October, but is quoted quarterly and
 * carries its own label.)
 */
export const RATES_REVIEW_BY = '2027-01-01';

/**
 * What was current when this file was last reviewed, item by item. Anything here that is NOT the
 * financial year above is deliberate and should say why — a figure whose new-year value has not been
 * published yet is legitimately behind, but it must be visible rather than assumed current.
 */
export const RATES_PROVENANCE = {
    agePension: '20 September 2026',
    // Assets-test limits, income free areas/cut-offs, deeming thresholds and the funeral-bond limit
    // index on 1 JULY; the pension RATE indexes 20 March / 20 September. Verified against Services
    // Australia's pages (each "last updated 1 July 2026") on 16 September 2026 — the file had sat on
    // the March figures for ten weeks, exactly as the aged-care schedule had (see agedCareSchedule.ts).
    assetAndIncomeTests: '1 July 2026',
    agedCareSchedule: '20 September 2026 (DoH schedule of fees and charges for residential care); MPIR and the 1 July 2014 maximum means-tested care fee from 1 October 2026',
    supportAtHomeSchedule: '20 September 2026',
    taxBrackets: '2026-27 (includes the 15% bracket legislated from 1 July 2026)',
    // The levy thresholds are legislated retrospectively each May (2025-26 is the latest set); the
    // surcharge tiers are published ahead (2026-27); LITO and SAPTO do not index.
    medicareLowIncomeThresholds: '2025-26 (ATO, 30 June 2026)',
    medicareLevySurcharge: '2026-27 (ATO)',
    lito: 'from 1 July 2022 (not indexed)',
    sapto: 'from 1 July 2024 (not indexed)',
} as const;

// --- Age Pension (Services Australia) - Indexed 20 March & 20 September ---
export const PENSION_AGE = 67;

export const PENSION_RATES = {
    single: 32180.20, // $1,237.70 per fortnight - as at 20 September 2026
    couple: 48516.00, // $1,866.00 per fortnight combined ($933.00 each)
};

/**
 * The two supplements EXCLUDED when income support is counted for the residential aged care means
 * test (they are part of the pension as paid, but not of aged-care assessable income).
 *
 * Sources: minimum pension supplement — Social Security Guide 5.2.8.10 (rates at 20 September
 * 2026, from the DSS "Social Security Payment Parameters — 20 September 2026" rates list). Energy
 * supplement — frozen since 20 September 2014 ($14.10/fn single, $10.60/fn each member of a
 * couple), and the single rates reconcile exactly: $29,520.40 basic + $2,293.20 pension supplement
 * + $366.60 energy = $32,180.20 = PENSION_RATES.single.
 *
 * "Single" applies to each member of an illness-separated couple as well — which is what a couple
 * with someone in residential care is.
 */
export const MINIMUM_PENSION_SUPPLEMENT = {
    single: 1235.00, // $47.50 per fortnight
    coupleEach: 930.80, // $35.80 per fortnight
};

export const ENERGY_SUPPLEMENT_PENSION = {
    single: 366.60,
    coupleEach: 275.60,
};

export const ASSET_TEST_LOWER_LIMITS = {
    single: 333000,
    couple: 499000,
};

export const ASSET_TEST_UPPER_LIMITS = {
    single: 745750,
    couple: 1121000,
};

/**
 * Detailed pension assets test limits from Services Australia, "Assets test for Age Pension",
 * full-pension limits effective 1 July 2026 (they index 1 July) and cut-offs effective
 * 20 September 2026 (they move with the pension rate, 20 March and 20 September). Use this object
 * when modelling non-homeowner and illness-separated scenarios.
 */
export const ASSET_TEST_LIMITS = {
    single: {
        homeowner: { full: 333000, cutoff: 745750 },
        nonHomeowner: { full: 600000, cutoff: 1012750 },
    },
    couple: {
        homeowner: { full: 499000, cutoff: 1121000 },
        nonHomeowner: { full: 766000, cutoff: 1388000 },
    },
    coupleIllnessSeparated: {
        homeowner: { full: 499000, cutoff: 1324500 },
        nonHomeowner: { full: 766000, cutoff: 1591500 },
    },
};

export const ANNUAL_REDUCTION_PER_1000_ASSETS = 3 * 26; // $3 per fortnight * 26 fortnights

export const INCOME_TEST_LOWER_LIMITS = {
    single: 5876, // $226 per fortnight — 1 July 2026
    couple: 10296, // $396 per fortnight combined — 1 July 2026
};

/**
 * Income test cut-off points (annualised) from Services Australia, "Income test for Age Pension",
 * as they stand from 20 September 2026. Each reconciles to the free area plus the 20 September
 * 2026 pension rate at the 50c taper (single: (2,701.40 − 226) × 0.5 = 1,237.70). They move again
 * on 20 March with the rate. Preferred over deriving cut-offs from rate / taper where
 * illness-separated rules apply.
 */
export const INCOME_TEST_CUTOFF_LIMITS = {
    single: 70236.40, // $2,701.40 per fortnight
    couple: 107328.00, // $4,128.00 per fortnight combined
    coupleIllnessSeparated: 139016.80, // $5,346.80 per fortnight combined
};

export const INCOME_REDUCTION_RATE = 0.50; // 50c for every dollar over

/** Services Australia "Deeming", thresholds from 1 July 2026 (they index 1 July; the RATES change 20 September). */
export const DEEMING_THRESHOLDS = {
    single: 66800,
    couple: 110600,
};

/** From 20 September 2026 (Australian Government Actuary determination): both rates rose 0.5 points. */
export const DEEMING_RATE_LOWER = 0.0175; // 1.75%
export const DEEMING_RATE_UPPER = 0.0375; // 3.75%

// --- Superannuation Minimum Pension Drawdown (2025-2026 FY) ---
export const MINIMUM_PENSION_DRAWDOWN_RATES = [
    { age: 64, rate: 0.04 },
    { age: 74, rate: 0.05 },
    { age: 79, rate: 0.06 },
    { age: 84, rate: 0.07 },
    { age: 89, rate: 0.09 },
    { age: 94, rate: 0.11 },
    { age: Infinity, rate: 0.14 },
];

// --- Income Tax (2026-27) ---
// Legislated tax cuts: the $18,201–$45,000 bracket falls 16% -> 15% from 1 Jul 2026, then 15% -> 14%
// from 1 Jul 2027. `base` = cumulative tax at each bracket floor (recomputed for the 15% rate:
// 26,800 × 15% = 4,020; +90,000 × 30% = 31,020; +55,000 × 37% = 51,370).
// ROADMAP (FY-aware projections): apply 14% for FY2027-28+ projection years.
export const TAX_BRACKETS = [
    { limit: 18200, rate: 0, base: 0 },
    { limit: 45000, rate: 0.15, base: 0 },
    { limit: 135000, rate: 0.30, base: 4020 },
    { limit: 190000, rate: 0.37, base: 31020 },
    { limit: Infinity, rate: 0.45, base: 51370 },
];

/**
 * The legislated step for 2027-28 and later: the $18,201–$45,000 bracket at 14% (Treasury Laws
 * Amendment (Cost of Living Tax Cuts) Act 2025). `base` recomputed: 26,800 × 14% = 3,752;
 * +90,000 × 30% = 30,752; +55,000 × 37% = 51,102. A projection year that opens in FY2027-28 or later
 * is taxed on these (F.4 of the actuarial review pack, fixed 17 Sep 2026).
 */
export const TAX_BRACKETS_FROM_2027_28 = [
    { limit: 18200, rate: 0, base: 0 },
    { limit: 45000, rate: 0.14, base: 0 },
    { limit: 135000, rate: 0.30, base: 3752 },
    { limit: 190000, rate: 0.37, base: 30752 },
    { limit: Infinity, rate: 0.45, base: 51102 },
];

/** The financial year (by its opening calendar year) the unqualified rate tables describe. */
export const RATES_FINANCIAL_YEAR_START = Number(RATES_FINANCIAL_YEAR.slice(0, 4));

/** The marginal brackets in force for the financial year opening in `fyStartYear` (2026 → 2026-27). */
export function taxBracketsForFinancialYear(fyStartYear?: number | null): typeof TAX_BRACKETS {
    const fy = fyStartYear ?? RATES_FINANCIAL_YEAR_START;
    return fy >= 2027 ? TAX_BRACKETS_FROM_2027_28 : TAX_BRACKETS;
}

export const MEDICARE_LEVY_RATE = 0.02;

/**
 * Medicare levy low-income thresholds. `upper` = `lower` × 1.25 (the levy shades in at 10c in the
 * dollar over `lower` until the full 2% applies at `upper`).
 *
 * These thresholds are set RETROSPECTIVELY: each May Budget legislates the figures for the financial
 * year just ending, so the latest legislated set always trails the projection's first year. The
 * engine carries the latest legislated set and indexes it. These are the 2025-26 figures, from the
 * ATO's "Medicare levy reduction for low-income earners" and "Medicare levy reduction – family
 * income" pages as last updated 30 June 2026 (owner-supplied 18 Sep 2026); the ATO's own worked
 * example on that page — Angie, single, $29,000, levy $98.90 — is pinned in incomeTax.test.ts.
 * The published upper figures are the lower × 1.25 rounded DOWN to the dollar, so they are held as
 * published rather than derived.
 *
 * SENIORS AND PENSIONERS entitled to SAPTO have their own, higher, thresholds (same source, same
 * year) — modelled since 17 Sep 2026; until then every senior was tested on the general figures and
 * over-charged the levy between the two lower thresholds.
 */
export const MEDICARE_LEVY_THRESHOLDS = {
    single: { lower: 28011, upper: 35013 },
    couple: { lower: 47238, upper: 59047 },
    singleSenior: { lower: 44268, upper: 55335 },
    coupleSenior: { lower: 61623, upper: 77028 },
    /** The family LOWER threshold rises by this for each dependent child or student — not modelled (D.3.30). */
    perDependentChild: 4338,
    /** …and the family UPPER threshold by this. */
    perDependentChildUpper: 5423,
};

/**
 * Medicare levy SURCHARGE — the extra 1% / 1.25% / 1.5% a person WITHOUT appropriate private
 * hospital cover pays once their income for surcharge purposes passes the base tier. Thresholds are
 * the ATO's for 2026-27 — the projection's first year — from "Medicare levy surcharge income
 * thresholds and rates" (owner-supplied 18 Sep 2026); they index each 1 July. The ATO's worked
 * example on that page — Tom, single, $117,000 for surcharge purposes, tier 1, $1,170 — is pinned
 * in medicareLevySurcharge.test.ts. Family thresholds apply to a member of a couple, and rise by
 * `perExtraChild` for each dependent child after the first (not modelled — D.3.30). Built
 * 17 Sep 2026 (F.19): private cover is asked for on the profile, and this is the only calculation
 * it drives — until then the engine wrongly treated cover as exempting the LEVY itself.
 */
export const MEDICARE_LEVY_SURCHARGE = {
    tiers: [
        { singleUpTo: 105000, familyUpTo: 210000, rate: 0 },
        { singleUpTo: 123000, familyUpTo: 246000, rate: 0.01 },
        { singleUpTo: 164000, familyUpTo: 328000, rate: 0.0125 },
        { singleUpTo: Infinity, familyUpTo: Infinity, rate: 0.015 },
    ],
    perExtraChild: 1500,
};

export const COMPANY_TAX_RATE = 0.30;

/**
 * Low Income Tax Offset (LITO) — resident individuals, 2025–26 FY (ATO).
 * Applied to income tax after franking credits; non-refundable.
 */
export const LITO = {
    maxOffset: 700,
    firstThreshold: 37500,
    secondThreshold: 45000,
    cutOut: 66667,
    firstTaperPerDollar: 0.05,
    secondTaperPerDollar: 0.015,
};

/**
 * Seniors and Pensioners Tax Offset (SAPTO) — 2025–26 FY rebate income thresholds (ATO indexed).
 * Offset reduces by SAPTO_TAPER_RATE for each $1 of rebate income above shade-in (until cut-out).
 * Eligibility in-app: age >= PENSION_AGE (rebate income approximated as taxable income).
 */
export const SAPTO_TAPER_RATE = 0.125;

export const SAPTO = {
    single: { maxOffset: 2230, shadeIn: 34919, cutOut: 52759 },
    coupleMember: { maxOffset: 1602, shadeIn: 30994, cutOut: 43810 },
    illnessSeparatedMember: { maxOffset: 2040, shadeIn: 33732, cutOut: 50052 },
};

// --- Gifting & Deprivation ---
export const GIFTING_LIMIT_ANNUAL = 10000;
export const GIFTING_LIMIT_5_YEAR = 30000;

// --- Downsizer Contribution (ATO) ---
export const DOWNSIZER_CONTRIBUTION_CAP = 300000;

// --- Transfer Balance Cap (ATO) — lifetime limit on amounts moved into retirement-phase pensions ---
/** The general TBC for 2026-27 ($2.1m after the 1 July 2026 indexation — it rose from $2.0m).
 *  Personal caps vary between $1.6m and this figure depending on when a person first commenced a
 *  retirement-phase pension. Indexes in $100,000 steps; check each 1 July. */
export const GENERAL_TRANSFER_BALANCE_CAP = 2100000;
/**
 * The defined benefit income cap (ATO): the general transfer balance cap ÷ 16. From age 60 a defined
 * benefit pension is tax-free up to it and 50% of the excess is assessable. $131,250 for 2026-27
 * ($125,000 in 2025-26); moves with the general cap each 1 July.
 */
export const DEFINED_BENEFIT_INCOME_CAP = GENERAL_TRANSFER_BALANCE_CAP / 16;
/** The screen the tool uses before ASKING about cap usage: $1.6m is the lowest personal cap anyone
 *  can have, so a person whose (current pensions + proposed commencement) sit under it cannot
 *  breach whatever their actual cap is — no question needed for that file. */
export const TRANSFER_BALANCE_CAP_SCREEN = 1600000;

// --- Super contribution caps (ATO), 2026-27 ---
/** Concessional (before-tax) cap, 2026-27. Indexes to AWOTE in $2,500 steps; was $30,000 in 25-26. */
export const CONCESSIONAL_CONTRIBUTION_CAP = 32500;
/**
 * Non-concessional (after-tax) cap, 2026-27 — four times the concessional cap. Verified against the
 * ATO for 2026-27; re-check each 1 July, because it moves with the concessional cap.
 */
export const NON_CONCESSIONAL_CONTRIBUTION_CAP = 130000;
/**
 * The last age at which a non-concessional contribution can be made: a person must be UNDER 75 on
 * 1 July of the financial year (in practice a fund can accept up to 28 days after the end of the
 * month they turn 75, which this tool does not attempt to model — it is a within-the-year timing
 * nicety, not a strategy lever). There is no work test for non-concessional contributions.
 */
export const NON_CONCESSIONAL_MAX_AGE = 75;
/**
 * Bring-forward: how many years' worth of the cap a person may contribute at once, by TOTAL SUPER
 * BALANCE at the previous 30 June. The bands are the general transfer balance cap less one and two
 * times the annual cap, so they move with both — do not hardcode them independently.
 *
 *   TSB below  $1.84m  ->  3 years  ($390,000)
 *   TSB below  $1.97m  ->  2 years  ($260,000)
 *   TSB below  $2.10m  ->  1 year   ($130,000, no bring-forward)
 *   TSB at or above the general TBC -> NIL: no non-concessional contribution may be made at all.
 *
 * Note the cap is FROZEN at the first year's rate for the whole bring-forward period — later
 * indexation does not lift a period already running.
 */
export const NON_CONCESSIONAL_BRING_FORWARD_YEARS = 3;

// --- Retirement Village (Services Australia - extra allowable amount for homeowner test) ---
/**
 * The difference between the non-homeowner and homeowner full-pension asset limits — by definition,
 * so it moves with them each 1 July: $600,000 − $333,000 from 1 July 2026 (was $258,000).
 */
export const EXTRA_ALLOWABLE_AMOUNT = 267000;

/**
 * Commonwealth Rent Assistance (CRA) — Services Australia. Paid ON TOP of the Age Pension to
 * NON-homeowners who pay private rent. Mechanism: 75c per $1 of rent (per fortnight) above
 * `minRent`, capped at `maxPayment`. Indexed 20 Mar & 20 Sep. All amounts are PER FORTNIGHT.
 */
export const RENT_ASSISTANCE = {
    effectiveDate: '2026-09-20',
    taperRate: 0.75,
    rates: {
        single: { minRent: 157.80, maxPayment: 223.80 },
        singleSharer: { minRent: 157.80, maxPayment: 149.20 },
        coupleCombined: { minRent: 255.80, maxPayment: 211.00 },
        // One of a couple separated due to illness / partner in respite care or prison → single rate.
        illnessSeparated: { minRent: 157.80, maxPayment: 223.80 },
        temporarilySeparated: { minRent: 157.80, maxPayment: 211.00 },
    },
};

// --- Funeral Bonds (exempt limit per person) ---
/** Services Australia "Funeral bonds and prepaid funerals": allowable limit as at 1 July 2026 (reviewed each 1 July). */
export const FUNERAL_BOND_LIMIT = 16250;

/**
 * THE 20 SEPTEMBER 2026 INDEXATION — APPLIED 21 September 2026. The pension rates, supplements,
 * cut-offs, deeming rates and rent assistance above are the DSS "Social Security Payment Parameters
 * — 20 September 2026 indexation" figures (staged and verified 16 September, applied on the day
 * after the Department of Health's 20 September residential schedule was published — see
 * agedCareSchedule.ts). Baselines re-struck with the deltas verified (goldenMaster.test.ts header).
 *
 * APPLIED 2 OCTOBER 2026 — 1 OCTOBER 2026, from the same DoH schedule: the Maximum Permissible Interest
 * Rate moves from 8.43% to 8.51% for entrants from 1 October, and the 1 July 2014 arrangements'
 * maximum means-tested care fee from $372.03 to $380.46 a day. Both are applied by date of entry in
 * law; the engine carries one figure, so copy these into `agedCareSchedule.ts` on 1 October and move
 * `RATES_REVIEW_BY` on. `ratesFreshness.test.ts` fails on the day to make sure.
 */
export const RATES_FROM_1_OCTOBER_2026 = {
    effectiveDate: '2026-10-01',
    /** 1 October – 31 December 2026 (DoH schedule from 20 September 2026). */
    MPIR: 0.0851,
    mpiQuarterLabel: '1 October 2026 – 31 December 2026',
    /** "Means tested care fee — from 1 October 2026" maximum daily rate (1 July 2014 arrangements). */
    maxMeansTestedCareFeeDaily: 380.46,
} as const;

// --- Projection assumptions ---
export const DEFAULT_INFLATION_ASSUMPTION_PCT = 2.5;

// --- Carer Allowance ---
export const CARER_ALLOWANCE_RATE = 3992.30; // Annual - $153.55 per fortnight

// --- Pre-1 Jan 2015 ABP life expectancy factors ---
export const LIFE_EXPECTANCY_FACTORS_ABP: Record<number, number> = {
    65: 19.96, 66: 19.22, 67: 18.49, 68: 17.77, 69: 17.05, 70: 16.34,
    71: 15.64, 72: 14.94, 73: 14.25, 74: 13.57, 75: 12.9, 76: 12.24,
    77: 11.59, 78: 10.95, 79: 10.33, 80: 9.72, 81: 9.12, 82: 8.54,
    83: 7.98, 84: 7.44, 85: 6.92, 86: 6.42, 87: 5.94, 88: 5.48,
    89: 5.04, 90: 4.62, 91: 4.22, 92: 3.84, 93: 3.49, 94: 3.16, 95: 2.85,
};

// --- Other Income Types (for fact-find) ---
export const OTHER_INCOME_TYPES = [
    { id: 'salary', name: 'Salary / Wages', assessable: true, taxable: true, manualEntry: true },
    { id: 'carerAllowance', name: 'Carer Allowance', assessable: false, taxable: false, manualEntry: false, rate: 3992.30, tooltip: 'A supplementary payment for those who provide daily care. It is non-taxable and not income/asset tested.' },
    { id: 'dvaDisability', name: 'DVA Disability Pension', assessable: false, taxable: false, manualEntry: true, tooltip: 'This is paid to compensate for injury or illness related to service. It is tax-free and not counted as income for the Age Pension income test.' },
    { id: 'dvaWarWidow', name: "DVA War Widow(er)'s Pension", assessable: true, taxable: false, manualEntry: true, tooltip: 'This pension is tax-free, but it IS counted as income for the Age Pension income test.' },
    { id: 'overseasPension', name: 'Overseas Pension', assessable: true, taxable: true, manualEntry: true, tooltip: 'Most overseas pensions are assessable for both the Centrelink income test and for Australian income tax.' },
    // A lifetime pension paid by a super fund's formula, not bought with a lump sum (UniSuper's Defined
    // Benefit Division, CSS, PSS, the military and state schemes). Its own rules — the capped tax-free
    // part for the income test, the pre-1998 asset-test exemption, the valuation factor where it is
    // counted, the defined benefit income cap for tax — live in definedBenefitPension.ts (15 Oct 2026).
    { id: 'definedBenefitPension', name: 'Lifetime pension from a super fund (defined benefit)', assessable: true, taxable: true, manualEntry: true, tooltip: "You didn't buy this with a lump sum: it is paid from your years in the fund, like CSS, PSS, UniSuper's Defined Benefit Division or a military scheme. Centrelink counts it as income less its tax-free part (capped at 10%), and usually not as an asset." },
    { id: 'boarders', name: 'Boarders / Lodgers', assessable: true, taxable: true, manualEntry: true, tooltip: 'Money from boarders or lodgers living in your home. Centrelink counts only part of it: 70% for a room only, 50% with breakfast, 20% with full board. Payments from your parent, child or sibling are not counted — leave those out.' },
    { id: 'other', name: 'Other Income', assessable: true, taxable: true, manualEntry: true, isCustom: true },
];

/**
 * The share of a boarder's or lodger's payment Centrelink counts as income (Social Security Guide
 * 4.3.8.40): the rest stands for the cost of what is provided. Payments from the homeowner's parent,
 * child or sibling are not counted at all — the profile asks for those to be left out.
 */
export const BOARD_INCOME_SHARE = { lodging: 0.7, bedAndBreakfast: 0.5, board: 0.2 } as const;
export function boardIncomeShare(boardType: unknown): number {
    return BOARD_INCOME_SHARE[boardType as keyof typeof BOARD_INCOME_SHARE] ?? BOARD_INCOME_SHARE.lodging;
}
